"use client";

import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import horizontalLogo from "..@/public/logo-2.svg";
import ukFlag from "..@/public/united-kingdom.png";
import frFlag from "..@/public/france.png";
import dzFlag from "..@/public/algeria.png";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, X } from "lucide-react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import Wishlist from "./wishlist";
import Cart from "./cart";

const locales = [
  { code: "en", name: "English", icon: ukFlag },
  { code: "ar", name: "العربية", icon: dzFlag },
  { code: "fr", name: "Français", icon: frFlag },
];
export const navigation = [
  { name_fr: "Accueil", name_en: "Home", name_ar: "الرئيسية", href: "/" },
  {
    name_fr: "Catalogue",
    name_en: "Catalog",
    name_ar: "الكتالوج",
    href: "/catalog",
  },
  {
    name_fr: "Contact",
    name_en: "Contact",
    name_ar: "اتصال",
    href: "/#contact",
  },
];

export default function Header() {
  const locale = useLocale();
  const t = useTranslations("header");
  const pathname = usePathname();
  const router = useRouter();
  const isHomePage = pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;

      // Check if the page is actually scrollable
      const isScrollable = documentHeight > windowHeight;

      if (!isScrollable) {
        // If page is not scrollable, treat as "not scrolled" (should show white icons on home page)
        setScrolled(false);
      } else {
        // If page is scrollable, use scroll position to determine state
        const scrollThreshold = Math.min(50, windowHeight * 0.05);
        setScrolled(offset > scrollThreshold);
      }
    };

    // Set initial state
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    window.addEventListener("load", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      window.removeEventListener("load", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  async function handleLocale(code) {
    await fetch("/api/set-locale", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ locale: code }),
    });
    document.cookie = `locale=${code}; path=/`;
    router.refresh();
  }

  // Debug logging
  useEffect(() => {
    console.log("Header state:", {
      isHomePage,
      scrolled,
      shouldShowWhite: isHomePage && !scrolled,
    });
  }, [isHomePage, scrolled]);

  return (
    <header
      className={cn(
        "fixed left-0 right-0 top-0 z-50 transition-colors duration-300",
        isHomePage && !mobileMenuOpen
          ? scrolled
            ? "bg-background/80 shadow-sm backdrop-blur-md"
            : "bg-transparent"
          : "bg-background shadow-sm",
      )}
    >
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8"
        aria-label="Global"
      >
        <div className="flex lg:flex-1">
          <Link href="/">
            <Image
              className="h-21 w-21 " //sm:h-10"
              src={horizontalLogo || "/placeholder.svg"}
              alt="logo"
              height={150}
              width={150}
            />
          </Link>
        </div>
        <div className="flex gap-2.5 lg:hidden">
          <Wishlist scrolled={isHomePage && !scrolled} />
          <Cart scrolled={isHomePage && !scrolled} />
          <Button
            variant="ghost"
            size="icon"
            className={isHomePage && !scrolled ? "text-white" : ""}
            onClick={toggleMobileMenu}
          >
            <Menu className="size-7" aria-hidden="true" />
          </Button>
        </div>
        <NavigationMenu className="hidden lg:flex">
          <NavigationMenuList className="gap-5 rtl:flex-row-reverse">
            {navigation.map((item) => (
              <NavigationMenuItem key={item.name_fr}>
                <NavigationMenuLink
                  href={item.href}
                  className={cn(
                    "whitespace-nowrap font-semibold leading-6 transition-colors",
                    isHomePage && !scrolled
                      ? "rounded pb-1 pl-2 pr-2 pt-1 text-white hover:bg-primary"
                      : "text-foreground hover:text-primary",
                  )}
                >
                  {locale === "fr"
                    ? item.name_fr
                    : locale === "ar"
                      ? item.name_ar
                      : item.name_en}
                </NavigationMenuLink>
              </NavigationMenuItem>
            ))}
          </NavigationMenuList>
        </NavigationMenu>
        <div className="hidden items-center space-x-4 lg:flex lg:flex-1 lg:justify-end">
          <Wishlist scrolled={isHomePage && !scrolled} />
          <Cart scrolled={isHomePage && !scrolled} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={isHomePage && !scrolled ? "text-white" : ""}
              >
                <Image
                  src={
                    locale === "fr" ? frFlag : locale === "ar" ? dzFlag : ukFlag
                  }
                  alt=""
                  className="w-5"
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {locales.map((locale) => (
                <DropdownMenuItem
                  key={locale.code}
                  onSelect={() => handleLocale(locale.code)}
                >
                  <Image
                    src={locale.icon || "/placeholder.svg"}
                    alt={`${locale.name} flag`}
                    className="mr-2 w-4"
                  />{" "}
                  {locale.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>
      {/* Mobile menu */}
      <div className={`lg:hidden ${mobileMenuOpen ? "block" : "hidden"}`}>
        <div className="fixed inset-0 z-50" />
        <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-background px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <Link href="/" className="-m-1.5 p-1.5">
              <Image
                className="h-150 w-150" // Set both height and width
                src={horizontalLogo || "/placeholder.svg"}
                alt="logo"
              />
            </Link>
            <Button variant="ghost" size="icon" onClick={toggleMobileMenu}>
              <span className="sr-only">Close menu</span>
              <X className="h-6 w-6" aria-hidden="true" />
            </Button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {navigation.map((item) => (
                  <Link
                    key={item.name_fr}
                    href={item.href}
                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-foreground hover:bg-muted"
                    onClick={toggleMobileMenu}
                  >
                    {locale === "fr"
                      ? item.name_fr
                      : locale === "ar"
                        ? item.name_ar
                        : item.name_en}
                  </Link>
                ))}
              </div>
              <div className="py-6">
                <div className="mt-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="lg" className="w-full">
                        <Image
                          src={
                            locale === "fr"
                              ? frFlag
                              : locale === "ar"
                                ? dzFlag
                                : ukFlag
                          }
                          alt=""
                          className="mr-2 w-5"
                        />
                        {t("locale-button")}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {locales.map((locale) => (
                        <DropdownMenuItem
                          key={locale.code}
                          onSelect={() => handleLocale(locale.code)}
                        >
                          <Image
                            src={locale.icon || "/placeholder.svg"}
                            alt={`${locale.name} flag`}
                            className="mr-2 w-4"
                          />{" "}
                          {locale.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
